import { useQueryClient } from '@tanstack/react-query'
import { RotateCw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { PULL_READY, watchPull } from '@/lib/pullToRefresh'
import { cn } from '@/lib/utils'
import { Loader } from '@/components/Loader'

// Long enough to see the spinner turn, so a quick refresh still reads as having happened.
const AT_LEAST = 600

// On a phone, pulling down from the top of a section's page (Home, People, Photos, Moments, Recipes) fetches it
// again, so whatever someone else added since shows up. A round spinner comes down from under the top bar with the
// finger, turns while the page is fetched, and goes back up.
export function PullToRefresh() {
  const queryClient = useQueryClient()
  const [distance, setDistance] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const [done, setDone] = useState(false)
  const busy = useRef(false)

  useEffect(
    () =>
      watchPull({
        onPull: (d) => {
          if (!busy.current) setDistance(d)
        },
        onRelease: (ready) => {
          if (busy.current) return
          if (!ready) return setDistance(0)
          busy.current = true
          setRefreshing(true)
          setDone(false)
          // Everything on the page asks the server again; a failure (no signal) keeps what's already showing.
          void Promise.all([
            queryClient.refetchQueries({ type: 'active' }),
            new Promise((resolve) => setTimeout(resolve, AT_LEAST)),
          ]).finally(() => {
            busy.current = false
            setRefreshing(false)
            setDistance(0)
            setDone(true)
          })
        },
      }),
    [queryClient],
  )

  const shown = refreshing ? PULL_READY : distance
  const ready = distance >= PULL_READY
  return (
    <>
      <div
        aria-hidden
        data-testid="pull-to-refresh"
        data-state={refreshing ? 'refreshing' : ready ? 'ready' : distance > 0 ? 'pulling' : 'idle'}
        className={cn(
          'pointer-events-none fixed top-[calc(var(--vv-top)+var(--bar-h)+var(--safe-top)-2.75rem)] left-1/2 z-10 grid size-10 place-items-center rounded-full border border-border/70 bg-background text-foreground shadow-float nav:hidden android:top-[calc(var(--vv-top)+3.5rem+var(--safe-top)-2.75rem)]',
          // Following the finger it moves with it; let go, it eases back.
          distance > 0 && !refreshing ? 'transition-none' : 'transition-[translate,opacity] duration-300 ease-page',
        )}
        style={{ translate: `-50% ${shown}px`, opacity: shown > 0 ? Math.min(1, shown / (PULL_READY * 0.6)) : 0 }}
      >
        {refreshing ? (
          <Loader label="Checking for anything new" size="sm" now />
        ) : (
          <RotateCw
            className={cn('size-5 stroke-[2.25]', !ready && 'opacity-60')}
            style={{ rotate: `${(distance / PULL_READY) * 270}deg` }}
          />
        )}
      </div>
      <p role="status" className="sr-only">
        {refreshing ? 'Checking for anything new…' : done ? 'Up to date.' : ''}
      </p>
    </>
  )
}
